import * as vp from 'vopay';
import type { PartnerAccountCreationResponse } from 'vopay';
import axios from 'axios';

import vopayApi from '../../../../services/api.config';
import { useDataFetchIndicator } from '../../../../hooks';

import type { HubspotData } from './Hubspot/Hubspot';
import type { BillingData } from './Billing/Billing';
import type { RiskData } from './Risk/Risk';
import { buildBillingPackageChangeReport } from './billingPackageChanges';

// The business-case endpoint validates these two free-text fields far more narrowly than their
// textareas suggest, and a single stray character fails the whole request — by which point the
// account itself has already been created. Both values are normalised to what it accepts:
//   Notes                          max 2000 chars, and the SSFR check rejects any of % \ / < > &.
//   BusinessModelAdditionalContext matched against a regex with no /s flag, so any newline fails it,
//                                  as does any run of 1000+ characters.
const NOTES_MAX_LENGTH = 1998;
const BUSINESS_MODEL_CONTEXT_MAX_LENGTH = 999;

const sanitizeNotes = (text: string): string => text.replace(/[%\\/<>&]/g, '').slice(0, NOTES_MAX_LENGTH);

const sanitizeBusinessModelContext = (text: string): string =>
  text.replace(/\s*\n+\s*/g, ' ').slice(0, BUSINESS_MODEL_CONTEXT_MAX_LENGTH);

type HookProps = { api: vp.Api };

export type R1SubmitData = {
  hubspot: HubspotData;
  billing: BillingData;
  risk: RiskData;
};

export type R1SubmitResult = {
  success: boolean;
  accountId: string;
  errorMessage: string;
  issues: string[];
  // Query string (status + balances) the account detail route needs, built from the fetched
  // account. Empty when the account wasn't created or couldn't be re-fetched.
  accountSearch: string;
};

type HookResult = { submit: (data: R1SubmitData) => Promise<R1SubmitResult> };

function hasAccountId(res: unknown): res is PartnerAccountCreationResponse {
  return Object.prototype.hasOwnProperty.call(res, 'AccountID');
}

type MatrixPair = { prefix: string; suffix: string };

// Every Enabled/Amount pair in PartnerAccountMatrixRequest — initialised to false/''
const ALL_MATRIX_PAIRS: MatrixPair[] = [
  { prefix: 'NTPFunding', suffix: 'EFT' },
  { prefix: 'NTPFunding', suffix: 'ACH' },
  { prefix: 'NTPFunding', suffix: 'ACHSameDay' },
  { prefix: 'NTPFunding', suffix: 'Interac' },
  { prefix: 'NTPFunding', suffix: 'CreditCard' },
  { prefix: 'NTPFunding', suffix: 'PayByBankEnabled' },
  { prefix: 'NTPFunding', suffix: 'AFT' },
  { prefix: 'NTPFunding', suffix: 'InboundEtransfer' },
  { prefix: 'NTPWithdraw', suffix: 'EFT' },
  { prefix: 'NTPWithdraw', suffix: 'ACH' },
  { prefix: 'NTPWithdraw', suffix: 'ACHSameDay' },
  { prefix: 'NTPWithdraw', suffix: 'Interac' },
  { prefix: 'NTPWithdraw', suffix: 'VPInstant' },
  { prefix: 'NTPWithdraw', suffix: 'Wire' },
  { prefix: 'NTPWithdraw', suffix: 'RTP' },
  { prefix: 'NTPWithdraw', suffix: 'P2C' },
  { prefix: 'NTPWithdraw', suffix: 'Fednow' },
  { prefix: 'NTPWithdraw', suffix: 'Paypal' },
  { prefix: 'NTPWithdraw', suffix: 'Venmo' },
  { prefix: 'NTPWithdraw', suffix: 'BillPay' },
  { prefix: 'NTPWithdraw', suffix: 'Crossborder' },
  { prefix: 'NTPWithdraw', suffix: 'ApplePay' },
  { prefix: 'NTPWithdraw', suffix: 'GooglePay' },
  { prefix: 'NTPWithdraw', suffix: 'RTR' },
  { prefix: 'NTPWithdraw', suffix: 'PushToCard' },
  { prefix: 'TPFundRequest', suffix: 'EFT' },
  { prefix: 'TPFundRequest', suffix: 'ACH' },
  { prefix: 'TPFundRequest', suffix: 'ACHSameDay' },
  { prefix: 'TPFundRequest', suffix: 'Interac' },
  { prefix: 'TPFundRequest', suffix: 'VPInstant' },
  { prefix: 'TPFundRequest', suffix: 'P2C' },
  { prefix: 'TPFundRequest', suffix: 'Wire' },
  { prefix: 'TPFundRequest', suffix: 'RTP' },
  { prefix: 'TPFunding', suffix: 'PayByBankEnabled' },
  { prefix: 'TPFunding', suffix: 'AFT' },
  { prefix: 'TPFunding', suffix: 'InboundEtransfer' },
  { prefix: 'TPCollectRequest', suffix: 'EFT' },
  { prefix: 'TPCollectRequest', suffix: 'ACH' },
  { prefix: 'TPCollectRequest', suffix: 'ACHSameDay' },
  { prefix: 'TPCollectRequest', suffix: 'Interac' },
  { prefix: 'TPCollectRequest', suffix: 'CreditCard' },
  { prefix: 'TPCollectRequest', suffix: 'PayByBankEnabled' },
  { prefix: 'TPCollectRequest', suffix: 'AFT' },
  { prefix: 'TPCollectRequest', suffix: 'InboundEtransfer' },
];

// Per permission key: all NTP + TP pairs to enable when that rail is on
const PERMISSION_TO_MATRIX: Record<string, MatrixPair[]> = {
  EFTCollectEnabled: [
    { prefix: 'NTPFunding', suffix: 'EFT' },
    { prefix: 'TPCollectRequest', suffix: 'EFT' },
  ],
  EFTSendEnabled: [
    { prefix: 'NTPWithdraw', suffix: 'EFT' },
    { prefix: 'TPFundRequest', suffix: 'EFT' },
  ],
  USDEFTCollectEnabled: [
    { prefix: 'NTPFunding', suffix: 'EFT' },
    { prefix: 'TPCollectRequest', suffix: 'EFT' },
  ],
  USDEFTSendEnabled: [
    { prefix: 'NTPWithdraw', suffix: 'EFT' },
    { prefix: 'TPFundRequest', suffix: 'EFT' },
  ],
  ACHFundEnabled: [
    { prefix: 'NTPFunding', suffix: 'ACH' },
    { prefix: 'TPCollectRequest', suffix: 'ACH' },
  ],
  ACHWithdrawEnabled: [
    { prefix: 'NTPWithdraw', suffix: 'ACH' },
    { prefix: 'TPFundRequest', suffix: 'ACH' },
  ],
  SameDayACHFundEnabled: [
    { prefix: 'NTPFunding', suffix: 'ACHSameDay' },
    { prefix: 'TPCollectRequest', suffix: 'ACHSameDay' },
  ],
  SameDayACHWithdrawEnabled: [
    { prefix: 'NTPWithdraw', suffix: 'ACHSameDay' },
    { prefix: 'TPFundRequest', suffix: 'ACHSameDay' },
  ],
  FednowWithdrawEnabled: [{ prefix: 'NTPWithdraw', suffix: 'Fednow' }],
  WireEnabled: [
    { prefix: 'NTPWithdraw', suffix: 'Wire' },
    { prefix: 'TPFundRequest', suffix: 'Wire' },
  ],
  RTPWithdrawEnabled: [
    { prefix: 'NTPWithdraw', suffix: 'RTP' },
    { prefix: 'TPFundRequest', suffix: 'RTP' },
  ],
  InteracMoneyRequestEnabled: [
    { prefix: 'NTPFunding', suffix: 'Interac' },
    { prefix: 'TPCollectRequest', suffix: 'Interac' },
  ],
  InteracBulkPayoutEnabled: [
    { prefix: 'NTPWithdraw', suffix: 'Interac' },
    { prefix: 'TPFundRequest', suffix: 'Interac' },
  ],
  RTRWithdrawEnabled: [
    { prefix: 'NTPWithdraw', suffix: 'VPInstant' },
    { prefix: 'NTPWithdraw', suffix: 'RTR' },
    { prefix: 'TPFundRequest', suffix: 'VPInstant' },
  ],
  PayByBankEnabled: [
    { prefix: 'NTPFunding', suffix: 'PayByBankEnabled' },
    { prefix: 'TPFunding', suffix: 'PayByBankEnabled' },
    { prefix: 'TPCollectRequest', suffix: 'PayByBankEnabled' },
  ],
  PushToCardEnabled: [
    { prefix: 'NTPWithdraw', suffix: 'PushToCard' },
    { prefix: 'NTPWithdraw', suffix: 'P2C' },
    { prefix: 'TPFundRequest', suffix: 'P2C' },
  ],
  AFTEnabled: [
    { prefix: 'NTPFunding', suffix: 'AFT' },
    { prefix: 'TPFunding', suffix: 'AFT' },
    { prefix: 'TPCollectRequest', suffix: 'AFT' },
  ],
  InboundEtransferEnabled: [
    { prefix: 'NTPFunding', suffix: 'InboundEtransfer' },
    { prefix: 'TPFunding', suffix: 'InboundEtransfer' },
    { prefix: 'TPCollectRequest', suffix: 'InboundEtransfer' },
  ],
  PaypalWithdrawEnabled: [{ prefix: 'NTPWithdraw', suffix: 'Paypal' }],
  VenmoWithdrawEnabled: [{ prefix: 'NTPWithdraw', suffix: 'Venmo' }],
  CreditCardEnabled: [
    { prefix: 'NTPFunding', suffix: 'CreditCard' },
    { prefix: 'TPCollectRequest', suffix: 'CreditCard' },
  ],
  BillPayEnabled: [{ prefix: 'NTPWithdraw', suffix: 'BillPay' }],
  CrossborderWithdrawEnabled: [{ prefix: 'NTPWithdraw', suffix: 'Crossborder' }],
  ApplePayEnabled: [{ prefix: 'NTPWithdraw', suffix: 'ApplePay' }],
  GooglePayEnabled: [{ prefix: 'NTPWithdraw', suffix: 'GooglePay' }],
};

function buildMatrixParams(accountId: string, risk: RiskData, billing: BillingData): vp.PartnerAccountMatrixRequest {
  // Merge ALL tab values — same spread used in the account request
  const formValues: Record<string, string | boolean> = {
    ...(billing.packageDetails.agreementDetails ?? {}),
    ...(billing.packageDetails.accountConfiguration ?? {}),
    ...(billing.packageDetails.accountSettings ?? {}),
    ...(billing.packageDetails.paymentRails ?? {}),
    ...(billing.packageDetails.additionalServices ?? {}),
  };

  const riskMatrix = risk.riskMatrix;

  // Initialise every field to false / '' so nothing is ever omitted
  const params: Record<string, unknown> = {
    VopayAccountID: accountId,
    AccountIntendedTransactionType: 'Sending and Collecting Money',
    FlowOfFunds: risk.flowOfFundsFileName || '',
  };

  for (const { prefix, suffix } of ALL_MATRIX_PAIRS) {
    params[`${prefix}${suffix}Enabled`] = false;
    params[`${prefix}${suffix}Amount`] = '';
    params[`${prefix}${suffix}ReturnChargebackPercent`] = '';
    params[`${prefix}${suffix}TransactionCount`] = '';
    params[`${prefix}${suffix}Frequency`] = 'monthly';
  }

  // Accumulate per-prefix volume + transaction count so the average is volume ÷ count.
  const totalsByPrefix: Record<string, { amount: number; count: number; largest: number }> = {
    NTPFunding: { amount: 0, count: 0, largest: 0 },
    NTPWithdraw: { amount: 0, count: 0, largest: 0 },
    TPFundRequest: { amount: 0, count: 0, largest: 0 },
    TPCollectRequest: { amount: 0, count: 0, largest: 0 },
  };

  for (const [key, value] of Object.entries(formValues)) {
    if (!key.endsWith('Enabled') || value !== true) continue;

    const mappings = PERMISSION_TO_MATRIX[key];
    if (!mappings) continue;

    const amount = riskMatrix[`${key}_amount`] ?? '';
    const count = riskMatrix[`${key}_count`] ?? '';
    const returnChargebackPercent = riskMatrix[`${key}_returnChargebackPercent`] ?? '';
    const frequency = riskMatrix[`${key}_frequency`] ?? '';

    for (const { prefix, suffix } of mappings) {
      params[`${prefix}${suffix}Enabled`] = true;
      if (amount) params[`${prefix}${suffix}Amount`] = amount;
      if (count) params[`${prefix}${suffix}TransactionCount`] = count;
      if (returnChargebackPercent) params[`${prefix}${suffix}ReturnChargebackPercent`] = returnChargebackPercent;
      if (frequency) params[`${prefix}${suffix}Frequency`] = frequency;

      const totals = totalsByPrefix[prefix];

      if (totals) {
        const numAmount = Number(amount);
        const numCount = Number(count);

        if (amount && !isNaN(numAmount)) {
          totals.amount += numAmount;
          totals.largest = Math.max(totals.largest, numAmount);
        }

        if (count && !isNaN(numCount)) totals.count += numCount;
      }
    }
  }

  // Set totals, averages (volume ÷ transaction count) and largest amounts for each prefix.
  for (const [prefix, { amount, count, largest }] of Object.entries(totalsByPrefix)) {
    if (amount === 0 && count === 0) continue;

    const avg = count > 0 ? amount / count : 0;

    let avgKey: string;
    let largestKey: string;

    if (prefix === 'NTPFunding') {
      avgKey = 'NTPFundingAvgFundedAmount';
      largestKey = 'NTPFundingLargestFundedAmount';
    } else if (prefix === 'NTPWithdraw') {
      avgKey = 'NTPWithdrawAvgWithdrawAmount';
      largestKey = 'NTPWithdrawLargestWithdrawAmount';
    } else {
      // For TPFundRequest and TPCollectRequest
      avgKey = `${prefix}AvgRequestAmount`;
      largestKey = `${prefix}LargestRequestAmount`;
    }

    params[`${prefix}TotalAmount`] = String(amount);
    params[avgKey] = avg.toFixed(2);
    params[largestKey] = String(largest);
  }

  return params as vp.PartnerAccountMatrixRequest;
}

// The document endpoints validate DocumentContent against /(data:)/i, so it must be a full data
// URI. UploadFile now emits one (keepDataUriPrefix), but guard here too so a value captured
function toDocumentDataUri(content: string, fileName: string): string {
  const trimmed = content.replace(/\s/g, '');
  if (trimmed.startsWith('data:')) return trimmed;
  const ext = fileName.split('.').pop()?.toLowerCase();

  const mimeByExt: Record<string, string> = {
    pdf: 'application/pdf',
    png: 'image/png',
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    gif: 'image/gif',
    webp: 'image/webp',
  };

  const mime = (ext && mimeByExt[ext]) || 'application/octet-stream';

  return `data:${mime};base64,${trimmed}`;
}

function useR1AccountSubmit({ api }: HookProps): HookResult {
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  const submit = async ({ hubspot, billing, risk }: R1SubmitData): Promise<R1SubmitResult> => {
    const source = axios.CancelToken.source();
    const details = hubspot.details;
    const form = hubspot.form;

    const platformUsage =
      String(billing.packageDetails.accountConfiguration?.PlatformUsage ?? '') || 'API + VoPay portal combined';

    // Step 1: Create account
    setDataFetchingInfo(true, 'Creating account...', 'fullScreen');

    const fullLegalName = `${details?.PrimaryContact.firstname ?? ''} ${details?.PrimaryContact.lastname ?? ''}`.trim();

    const accountRes = await api.postPartnerAccount(
      vp.removeEmptyProperty<vp.PartnerAccountRequest>({
        Name: details?.BusinessInformation.CompanyName ?? '',
        LegalBusinessName: details?.BusinessInformation.CompanyName ?? '',
        BusinessName: details?.BusinessInformation.Dba || details?.BusinessInformation.CompanyName || '',
        EmailAddress: details?.PrimaryContact.email ?? '',
        FullLegalName: fullLegalName,
        PrimaryContactFullName: fullLegalName,
        Country: details?.CompanyProfile.Country ?? '',
        ClientAccountsEnabled: false,
        OrganizationalType: '',
        BusinessTypeID: '',
        BusinessTypeCategoryID: '',
        AccountingSoftwareIntegrationEnabled: false,
        Address1: '',
        City: '',
        Province: '',
        PostalCode: '',
        HubspotCompany_ID: details?.CompanyProfile.CompanyHubspotID,
        HubspotDeal_ID: form.hubSpotDealId,
        WebsiteURL: details?.BusinessInformation.CompanyWebsite
          ? vp.sanatizeURL(details.BusinessInformation.CompanyWebsite)
          : undefined,
        BusinessPhone: details?.BusinessInformation.CompanyPhone,
        BillingPackageID: billing.package,
        SendWelcomeEmail: false,
        ...(billing.packageDetails.agreementDetails ?? {}),
        ...(billing.packageDetails.accountConfiguration ?? {}),
        ...(billing.packageDetails.accountSettings ?? {}),
        ...(billing.packageDetails.paymentRails ?? {}),
        ...(billing.packageDetails.additionalServices ?? {}),
      }),
      source,
    );

    if (!accountRes.Success || !hasAccountId(accountRes)) {
      setDataFetchingInfo(false);

      return {
        success: false,
        accountId: '',
        errorMessage: accountRes.ErrorMessage || 'Account creation failed',
        issues: [],
        accountSearch: '',
      };
    }

    setDataFetchingInfo(false);

    const accountId = accountRes.AccountID;
    const issues: string[] = [];

    // Runs a post-creation sub-request; on failure it records a message instead of throwing,
    // so a single failed step doesn't abort the rest (the account already exists).
    const capture = async <T extends { Success: boolean; ErrorMessage: string }>(
      label: string,
      request: Promise<T>,
    ): Promise<T | null> => {
      try {
        const res = await request;
        if (!res.Success) issues.push(res.ErrorMessage || `${label} failed.`);

        return res;
      } catch {
        issues.push(`${label} failed.`);

        return null;
      }
    };

    // Step 2: In parallel — fetch account token, update HubSpot, submit matrix
    setDataFetchingInfo(true, 'Submitting account data...', 'fullScreen');

    const [accountsRes] = await Promise.all([
      capture('Account details', api.fetchPartnerAccounts({ VopayAccountID: accountId, ShowToken: true }, source)),
      capture(
        'HubSpot deal update',
        api.postHubSpotDealUpdate(
          vp.removeEmptyProperty<vp.HubSpotDealUpdateRequest>({
            DealID: form.hubSpotDealId,
            PlatformUsage: platformUsage,
            CompanyWebsite: details?.BusinessInformation.CompanyWebsite
              ? vp.sanatizeURL(details.BusinessInformation.CompanyWebsite)
              : undefined,
            CompanyPhone: details?.BusinessInformation.CompanyPhone,
            SocialMediaPage: details?.BusinessInformation.SocialMediaPage
              ? vp.sanatizeURL(details.BusinessInformation.SocialMediaPage)
              : undefined,
          }),
          source,
        ),
      ),
      capture('Payment rail matrix', api.postPartnerAccountMatrix(buildMatrixParams(accountId, risk, billing), source)),
    ]);

    setDataFetchingInfo(false);
    // Step 3: In parallel — submit business case and upload document
    setDataFetchingInfo(true, 'Finalising account...', 'fullScreen');

    const { fromAccounts } = vp.dataTransformers;
    const accounts = accountsRes ? fromAccounts(accountsRes.Accounts) : null;
    const token = accounts?.toAccountDetails[accountId]?.token ?? '';

    // Build the status + balances query string the account detail route expects.
    const createdBalance = accounts?.toBalances.find((b) => b.AccountID === accountId);

    const accountSearch = createdBalance
      ? new URLSearchParams({
          accountId: createdBalance.AccountID,
          isActive: String(createdBalance.IsActive),
          accountStatus: createdBalance.AccountStatus,
          currency: createdBalance.Currency,
          pending: createdBalance.PendingBalance,
          available: createdBalance.AvailableBalance,
          account: createdBalance.AccountBalance,
          reserve: createdBalance.Reserve,
          immediate: createdBalance.AvailableImmediately,
        }).toString()
      : '';

    const businessName = details?.BusinessInformation.CompanyName ?? '';

    const changeReport = buildBillingPackageChangeReport(billing, details?.CompanyProfile.Country ?? '');
    // The user's notes lead so that, if the two together overrun the field, it's the regenerable
    // change report that gets cut rather than anything the user typed.
    const businessCaseNotes = sanitizeNotes([form.additionalNotes, changeReport].filter(Boolean).join('\n\n'));

    await Promise.all([
      capture(
        'Business case',
        api.postPartnerAccountBusinessCase(
          vp.removeEmptyProperty<vp.PartnerAccountBusinessCaseRequest>({
            VopayAccountID: accountId,
            BusinessName:
              businessName ||
              `${details?.PrimaryContact.firstname ?? ''} ${details?.PrimaryContact.lastname ?? ''}`.trim(),
            FirstName: details?.PrimaryContact.firstname,
            LastName: details?.PrimaryContact.lastname,
            BusinessEmail: details?.PrimaryContact.email ?? '',
            BusinessPhone: details?.BusinessInformation.CompanyPhone,
            BillingPackagesID: billing.package,
            WebsiteURL: details?.BusinessInformation.CompanyWebsite
              ? vp.sanatizeURL(details.BusinessInformation.CompanyWebsite)
              : undefined,
            BusinessModel: undefined,
            BusinessModelCollectingFromThirdParties: risk.businessModelCollectingFromThirdParties || undefined,
            BusinessModelFundingFromOwnAccount: risk.businessModelFundingFromOwnAccount || undefined,
            BusinessModelDistributingToThirdParties: risk.businessModelDistributingToThirdParties || undefined,
            BusinessModelActingAsIntermediary: risk.businessModelActingAsIntermediary || undefined,
            BusinessModelAdditionalContext:
              sanitizeBusinessModelContext(risk.businessModelAdditionalContext) || undefined,
            NumberofYearsInBusiness: undefined,
            Industry: undefined,
            MSB: undefined,
            ISO: undefined,
            Notes: businessCaseNotes || undefined,
            PlatformUsage: platformUsage,
            DataAggregator: String(billing.packageDetails.accountConfiguration?.DataAggregator ?? '') || undefined,
            SystemOfRecord: String(billing.packageDetails.accountConfiguration?.SystemOfRecord ?? '') || undefined,
            ReserveToActivate: undefined,
            createBillingPackage: false,
            SocialMediaPage: details?.BusinessInformation.SocialMediaPage
              ? vp.sanatizeURL(details.BusinessInformation.SocialMediaPage)
              : undefined,
            CompanyPhone: details?.BusinessInformation.CompanyPhone || '',
            LegalAgreementNotes:
              String(billing.packageDetails.agreementDetails?.LegalAgreementNotes ?? '') || undefined,
          }),
          source,
        ),
      ),
      risk.flowOfFundsFile && token
        ? capture(
            'Flow of funds upload',
            api.postAccountDocumentUpload(
              {
                DocumentContent: toDocumentDataUri(risk.flowOfFundsFile, risk.flowOfFundsFileName),
                DocumentName: `Flow of Funds - ${businessName}`,
                DocumentType: 'flowoffunds',
              },
              token,
              source,
            ),
          )
        : Promise.resolve(null),
    ]);

    setDataFetchingInfo(false);

    return { success: true, accountId, errorMessage: '', issues, accountSearch };
  };

  return { submit };
}

export default vopayApi.withApiHook<HookProps, HookResult>(useR1AccountSubmit);
